import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Info, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Alert — Volume 03 §21. Thông tin tồn tại trong layout.
 * Cảnh báo quan trọng lặp bằng text + icon (không chỉ dựa màu — Volume 02 §16.4).
 */
const alertVariants = cva("flex gap-3 rounded-md border p-4", {
  variants: {
    variant: {
      info: "border-info/30 bg-blue-50 dark:bg-info/10",
      success: "border-success/30 bg-green-50 dark:bg-success/10",
      warning: "border-warning/30 bg-orange-50 dark:bg-warning/10",
      danger: "border-danger/30 bg-red-50 dark:bg-danger/10",
    },
  },
  defaultVariants: { variant: "info" },
});

const ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
} as const;

const ICON_COLOR = {
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
} as const;

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  title?: string;
}

export function Alert({ className, variant = "info", title, children, ...props }: AlertProps) {
  const v = variant ?? "info";
  const Icon = ICONS[v];
  return (
    <div role="alert" className={cn(alertVariants({ variant: v }), className)} {...props}>
      <Icon className={cn("mt-0.5 size-5 shrink-0", ICON_COLOR[v])} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {title && <p className="text-label font-semibold text-foreground">{title}</p>}
        {children && (
          <div className={cn("text-body-sm text-foreground-secondary", title && "mt-1")}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
